import React from "react";
import styled, { keyframes } from "styled-components";
import { colors } from "./colors";

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

/**
 * The track is the full ring and the head is one coloured quarter of it, so a single
 * element produces the arc without a second node or an SVG.
 *
 * A `span`, not a `div`: most of these spinners live inside a `<button>`, whose content model
 * is phrasing content only. `react-bootstrap`'s call sites all passed `as="span"` for this
 * reason. `inline-block` keeps it behaving like a block for sizing, and as a flex item it is
 * unaffected either way.
 */
/**
 * `vertical-align` matters because most of these sit beside a label. The browser default,
 * baseline, left the ring 3px above where react-bootstrap's sat. Measured inside a .btn:
 * Bootstrap landed 14px from the button top, middle lands at 15, sub reproduces 14 exactly.
 * middle takes the 1px, because a subscript alignment on a spinner would puzzle the next
 * reader.
 */
const Ring = styled.span`
    display: inline-block;
    vertical-align: middle;
    box-sizing: border-box;
    width: ${({ $size }) => $size}px;
    height: ${({ $size }) => $size}px;
    border: ${({ $thickness }) => $thickness}px solid ${({ $track }) => $track};
    border-top-color: ${({ $head }) => $head};
    border-radius: 50%;
    animation: ${spin} 0.6s linear infinite;
    flex-shrink: 0;
`;

/**
 * Ask for the surrounding text colour instead of a token. Named rather than implied on
 * purpose: inheriting by default is what made `react-bootstrap`'s spinner render a different
 * colour in every context without anyone choosing it. Asked for explicitly, it is the right
 * answer in one case — a spinner beside a label whose colour is set by CSS this component
 * does not own, such as Bootstrap's `.btn-*` classes, where matching the label is the whole
 * requirement.
 */
export const INHERIT = "inherit";

/**
 * `colors.js` holds two shapes: tier scales (`l3rnTech`, `neutral`, …) and plain values
 * (`white`). Reading `[900]` off a plain value yields `undefined`, which used to reach the
 * CSS silently — the same class of bug as `react-bootstrap` accepting `variant="l3rnTech"`
 * and doing nothing with it. So both shapes resolve here, and a name that is in neither
 * says so out loud in development rather than rendering a plausible default.
 */
const resolveHead = (variant) => {
    if (variant === INHERIT) return "currentColor";

    const value = colors[variant];

    if (typeof value === "string") return value;
    if (value && value[900]) return value[900];

    if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(
            `Spinner: unknown variant "${variant}". Falling back to l3rnTech. ` + `Use a key from ui-kit/colors.js.`
        );
    }

    return colors.l3rnTech[900];
};

/**
 * An indeterminate loading indicator, and the kit's only one.
 *
 * Replaces `react-bootstrap`'s `Spinner`, where `variant` was a Bootstrap theme name — so
 * `variant="l3rnTech"` emitted a `text-l3rnTech` class that no stylesheet defined and the
 * brand colour never applied.
 *
 * @param {number}  size        outer diameter in px, border included
 * @param {string}  variant     a `colors.js` key; falls back to `l3rnTech` and warns
 * @param {number}  thickness   ring width in px
 * @param {string}  label       announced by screen readers while it spins
 * @param {boolean} decorative  set when a visible label already says what is happening —
 *                              renders `aria-hidden` and no `role`, so the spinner is not
 *                              announced a second time. This is the in-button case.
 * @param {string}  trackColor  the unfilled part of the ring. Intended for kit components
 *                              composing the spinner onto a surface the palette's default
 *                              track does not read against; feature code should use
 *                              `variant` and leave this alone.
 * @param {string}  headColor   overrides `variant`. Same intent as `trackColor`: for kit
 *                              components that have already resolved a colour from tokens,
 *                              such as `Button`, which colours the spinner with the same
 *                              helper that colours its icons.
 */
/**
 * `forwardRef` because four call sites hand the spinner to an intersection observer as the
 * sentinel that triggers the next page — a function component cannot take a ref, and React
 * would have dropped it with a warning while the observer silently never fired.
 */
const Spinner = React.forwardRef(
    (
        {
            size = 24,
            variant = "l3rnTech",
            thickness = 2,
            label = "Loading",
            decorative = false,
            trackColor,
            headColor,
            className,
        },
        ref
    ) => {
        const announcement = decorative ? { "aria-hidden": true } : { role: "status", "aria-label": label };

        // Inheriting the head means the track cannot be a token either — a grey ring around a
        // white-on-blue button reads as a bug. Bootstrap solved this by leaving the rest of the
        // ring transparent, which is also what these call sites render today, so parity and
        // correctness agree here.
        const track = trackColor || (variant === INHERIT ? "transparent" : colors.neutral[200]);

        return (
            <Ring
                ref={ref}
                className={className}
                {...announcement}
                $size={size}
                $thickness={thickness}
                $track={track}
                $head={headColor || resolveHead(variant)}
            />
        );
    }
);

Spinner.displayName = "Spinner";

export default Spinner;
